import { TimeBlockElementSelectorService } from '../../../../../rendering/time-block-element-selector.service';
import { CSSDraggedClass } from '../../../../../../../util/calendar-style.constants';
import { TimeBlockCrudService } from '../../../../../crud/time-block-crud.service';
import { TimeBlockDragResizeControllerService } from '../../time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { DragDropPositioningService } from './drag-drop-positioning.service';
import { DragAndDropHandlerService } from './drag-and-drop-handler.service';
import { TimeBlockStructureService } from '../../../../../time-block-structure/time-block-structure.service';
import {
  IFixIntervalViewType,
  IVariableIntervalViewType,
  TimeBlockType,
} from '../../../../../types/time-block-types';
import { ITimeBlockComponentItem } from '../../../../../types/time-block-component-item-types';
import { RendererStyleFlags2 } from '@angular/core';
import { CssAttributes } from '../../../../../rendering/css-attributes';
import { TimeBlockService } from '../../../../../services/time-block.service';
import { SharedHorizontalDragResizeService } from '../../time-block-drag-resize-handling/shared-horizontal-drag-resize.service';
import { TimeBlockDayOrWeekVariableIntervalType } from '../../../../../../../../../core/models/timeblock/time-block-view-type.model';
import { ShiveRenderService } from '../../../../../../../../../core/services/rendering/shive-render.service';

export abstract class AbstractDragController {
  protected dXAbs = 0; // / The total amount of pixels the block moved in x-direction since the drag started.
  protected dYRelAggr = 0;
  protected draggedParts = new Array<ITimeBlockComponentItem>();

  protected constructor(
    private readonly tbCrudService: TimeBlockCrudService,
    private readonly tbStructureService: TimeBlockStructureService,
    private readonly dragDropPosService: DragDropPositioningService,
    private readonly shRenderService: ShiveRenderService,
    private readonly tbDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly tbService: TimeBlockService,
    private readonly dAndDHandlerService: DragAndDropHandlerService,
    private readonly sharedDAndRService: SharedHorizontalDragResizeService,
  ) {}

  public beforeStartDragging(): void {
    const draggedTimeBlock = this.tbDragResizeControllerService.TransformationTimeBlock;
    if (draggedTimeBlock.timeBlockModel.fixInterval !== null) {
      const tbModel = draggedTimeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType;
      this.dAndDHandlerService.sourceContainerId = tbModel.laneIndexStart;
      this.dAndDHandlerService.targetContainerId = tbModel.laneIndexStart;
    } else {
      const tbModel = draggedTimeBlock.timeBlockModel
        .timeBlockViewType as IVariableIntervalViewType;
      this.dAndDHandlerService.sourceContainerId = tbModel.laneIndex;
      this.dAndDHandlerService.targetContainerId = tbModel.laneIndex;
      this.draggedParts = this.tbStructureService.retrieveTimeBlockPart(
        draggedTimeBlock.id,
        TimeBlockType.TransformingBlock,
        -1,
      ) as ITimeBlockComponentItem[];
    }

    // Add CSS classes for all dragged time block parts and also the width.
    const draggingBlock = this.tbDragResizeControllerService.TransformationTimeBlock;
    const timeBlockWidth =
      draggingBlock.timeBlockModel.componentRef.instance.timeBlockHTMLWrapper.nativeElement
        .clientWidth;

    this.draggedParts.forEach((timeBlockPart) => {
      const timeBlockHTMLWrapper =
        TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlockPart);
      this.shRenderService.addClass(timeBlockHTMLWrapper, CSSDraggedClass);

      // If this dragging part is outside the view, copy the width of the visible dragging part since it has a width of 0.
      if (timeBlockHTMLWrapper.clientWidth < timeBlockWidth) {
        this.shRenderService.setStyle(
          timeBlockHTMLWrapper,
          CssAttributes.width,
          `${timeBlockWidth}px`,
          RendererStyleFlags2.Important,
        );
      }
    });
  }

  public afterDragging(event: Interact.DragEvent): void {
    const transformationTimeBlock = this.tbDragResizeControllerService.TransformationTimeBlock;

    // Remove transparent indicator block (ghost block)
    if (this.tbDragResizeControllerService.GhostTimeBlock) {
      this.tbCrudService.removeTimeBlock(
        this.tbDragResizeControllerService.GhostTimeBlock,
        TimeBlockType.GhostBlock,
      );
      this.tbDragResizeControllerService.GhostTimeBlock = null;
    }

    // Remove dragging block from calendar model
    if (this.tbService.isTimeBlockOutsideOfView(transformationTimeBlock)) {
      return;
    }

    this.tbCrudService.removeTimeBlock(transformationTimeBlock, TimeBlockType.TransformingBlock);

    // Set the dragged block or blocks
    transformationTimeBlock.timeBlockModel.type = TimeBlockType.ExistingBlock;
    if (transformationTimeBlock.timeBlockModel.fixInterval !== null) {
      this.dragDropPosService.deployFixIntervalTimeBlock(transformationTimeBlock);
    } else if (
      transformationTimeBlock.timeBlockModel.timeBlockViewType instanceof
      TimeBlockDayOrWeekVariableIntervalType
    ) {
      this.dragDropPosService.deployDayOrWeekVariableIntervalTimeBlocks(event, this.draggedParts);
    }

    // Reset everything
    this.dXAbs = 0;
    this.dYRelAggr = 0;
    this.sharedDAndRService.lastMouseLaneIndex = -1;
  }
}
